import PropTypes from "prop-types";
import { useFieldArray, useFormContext, useWatch } from "react-hook-form";

import DeleteIcon from "@mui/icons-material/Delete";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { Box, Stack, Tooltip, Typography } from "@mui/material";
import { DataGrid, GridActionsCellItem } from "@mui/x-data-grid";

import { ProductVariantNameCell, useSnackbar } from "@/features/ui";
import { formatNumber } from "@/features/shared";

export const ProductVariantsSection = ({ editMode = false }) => {
  const { control } = useFormContext();
  const { showSnackbar } = useSnackbar();

  const { update, remove } = useFieldArray({
    control,
    name: "deliveryOrderProductVariants",
  });

  const productVariants = useWatch({
    control,
    exact: true,
    name: "deliveryOrderProductVariants",
  });

  const handleDeleteClick = (id) => () => {
    // 🔍 Find index of the row with the matching id
    const index = productVariants.findIndex((variant) => variant.id === id);

    if (index !== -1) {
      remove(index);
      showSnackbar("Produk berhasil dihapus", "success");
    }
  };

  const columns = [
    {
      field: "name",
      headerName: "Nama",
      flex: 5,
      renderCell: ({ row: { name, sku } }) => (
        <ProductVariantNameCell name={name} sku={sku} />
      ),
    },
    ...(editMode
      ? [
          {
            field: "orderedQuantity",
            headerName: "Dipesan",
            type: "number",
            flex: 1,
            valueGetter: (_, row) =>
              row.salesOrderProductVariant?.quantity || 0,
          },
        ]
      : []),
    {
      field: "quantity",
      headerName: "Qty",
      type: "number",
      valueFormatter: (value) => formatNumber(value),
      flex: 1.5,
      editable: true,
    },
    { field: "unitOfMeasurement", headerName: "Unit", flex: 1 },
    ...(editMode
      ? [
          {
            field: "invoicedQuantity",
            headerName: "Difaktur",
            type: "number",
            valueFormatter: (value) => formatNumber(value),
            flex: 1,
          },
          {
            field: "returnedQuantity",
            headerName: "Diretur",
            type: "number",
            valueFormatter: (value) => formatNumber(value),
            flex: 1,
          },
        ]
      : []),
    {
      field: "note",
      headerName: "Catatan",
      flex: 2,
      editable: true,
    },
    {
      field: "actions",
      type: "actions",
      getActions: ({ id, row }) => {
        // In edit mode, check if can delete: both returnedQuantity and invoicedQuantity must be 0
        const canDelete = editMode
          ? row.returnedQuantity === 0 && row.invoicedQuantity === 0
          : true;

        // Build reason message for disabled state
        const reasons = [];
        if (editMode && row.invoicedQuantity > 0) {
          reasons.push("sudah difaktur");
        }
        if (editMode && row.returnedQuantity > 0) {
          reasons.push("sudah diretur");
        }
        const tooltipTitle = canDelete
          ? "Hapus"
          : `Tidak dapat dihapus (${reasons.join(", ")})`;

        return [
          <Tooltip key={id} title={tooltipTitle} arrow>
            <span>
              <GridActionsCellItem
                icon={<DeleteIcon />}
                label="Hapus"
                className="textPrimary"
                onClick={handleDeleteClick(id)}
                color="error"
                disabled={!canDelete}
              />
            </span>
          </Tooltip>,
        ];
      },
    },
  ];

  const processRowUpdate = async (newRow) => {
    return new Promise((resolve, reject) => {
      const { quantity, maxQuantity, minQuantity } = newRow;

      // Validation logic
      if (quantity > maxQuantity) {
        reject(
          new Error(
            `Kuantitas tidak boleh lebih besar dari pesanan (max: ${maxQuantity})`,
          ),
        );
        return;
      }

      if (minQuantity !== undefined && quantity < minQuantity) {
        reject(
          new Error(
            `Kuantitas tidak boleh kurang dari ${minQuantity} (sudah dikembalikan/difakturkan)`,
          ),
        );
        return;
      }

      if (quantity < 1) {
        reject(new Error("Kuantitas tidak boleh kurang dari 1"));
        return;
      }

      // Find the index and update the form state
      const index = productVariants.findIndex(
        (variant) => variant.id === newRow.id,
      );

      if (index !== -1) {
        update(index, newRow);
        resolve(newRow);
      } else {
        reject(new Error("Produk tidak ditemukan. Muat ulang halaman lalu coba lagi."));
      }
    });
  };

  const handleProcessRowUpdateError = (error) => {
    showSnackbar(error.message, "error");
  };

  return (
    <Stack spacing={1}>
      <DataGrid
        getRowHeight={() => "auto"}
        editMode="row"
        columns={columns}
        rows={productVariants}
        processRowUpdate={processRowUpdate}
        onProcessRowUpdateError={handleProcessRowUpdateError}
        isCellEditable={(params) => {
          // In edit mode, disable editing if invoiced or returned
          if (editMode) {
            return (
              params.row.invoicedQuantity === 0 &&
              params.row.returnedQuantity === 0
            );
          }
          return true;
        }}
        initialState={{
          sorting: {
            sortModel: [{ field: "name", sort: "asc" }],
          },
        }}
      />
      {editMode && (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 0.5
          }}>
          <InfoOutlinedIcon fontSize="small" color="action" />
          <Typography variant="caption" sx={{
            color: "text.secondary"
          }}>
            Produk yang sudah difaktur atau diretur tidak dapat diubah atau
            dihapus
          </Typography>
        </Box>
      )}
    </Stack>
  );
};

ProductVariantsSection.propTypes = {
  editMode: PropTypes.bool,
};
